// v1
// Path: frontend/src/components/QuickBooksSync/ConnectionDetailsModal.jsx
// Inputs: token, account, onClose, onAccountUpdated
// Outputs: React JSX modal displaying OAuth status, token expiration, credentials, and re-authorization triggers
// Summary: Credentials and OAuth diagnostics modal allowing admins to test connectivity, re-authorize with Intuit, or update client secrets.

import React, { useState } from 'react';

export const ConnectionDetailsModal = ({ token, account, onClose, onAccountUpdated }) => {
    const [loadingTest, setLoadingTest] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState({
        accountName: account?.AccountName || '',
        environment: account?.Environment || 'sandbox',
        clientId: account?.ClientID || '',
        clientSecret: '',
        realmId: account?.RealmID || '',
        redirectUri: account?.RedirectUri || 'https://admin.sbs-roc.com/api/quickbooks/callback'
    });
    const [saving, setSaving] = useState(false);

    const handleAuthorize = async () => {
        try {
            const res = await fetch(`/api/quickbooks/auth-url/${account.AccountID}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data.authUrl) {
                    window.location.href = data.authUrl;
                }
            } else {
                const err = await res.json();
                alert(`Error starting OAuth: ${err.error || 'Unknown error'}`);
            }
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    const handleTestConnection = async () => {
        setLoadingTest(true);
        setTestResult(null);
        try {
            const res = await fetch(`/api/quickbooks/test-connection/${account.AccountID}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setTestResult(data);
            if (data.success && onAccountUpdated) {
                onAccountUpdated();
            }
        } catch (err) {
            setTestResult({ success: false, error: err.message });
        } finally {
            setLoadingTest(false);
        }
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await fetch('/api/quickbooks/accounts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    accountId: account.AccountID,
                    ...editForm
                })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to update credentials');
            }
            if (onAccountUpdated) onAccountUpdated();
            setShowEdit(false);
        } catch (err) {
            alert(`Save error: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    if (!account) return null;

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-slate-700 flex justify-between items-center bg-slate-800/90">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
                            <span className="material-symbols-outlined">key</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-white">{account.AccountName}</h3>
                                <span className={`px-2 py-0.5 text-[10px] font-semibold rounded uppercase ${account.Environment === 'sandbox' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'}`}>
                                    {account.Environment}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">QuickBooks Online OAuth 2.0 Credentials & Diagnostics</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 overflow-y-auto flex-1 space-y-4 text-xs">
                    {/* Status Ribbon */}
                    <div className={`p-3.5 rounded-lg border flex items-center justify-between ${account.IsConnected ? 'bg-emerald-950/40 border-emerald-800/60 text-emerald-300' : 'bg-amber-950/40 border-amber-800/60 text-amber-300'}`}>
                        <div className="flex items-center gap-2.5">
                            <span className={`w-3 h-3 rounded-full ${account.IsConnected ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-amber-400'}`}></span>
                            <div>
                                <div className="font-bold text-sm text-white">
                                    {account.IsConnected ? 'Authorized & Connected' : 'Authorization Required'}
                                </div>
                                <div className="text-[11px] opacity-80">
                                    {account.IsConnected ? (account.CompanyName ? `Linked to ${account.CompanyName}` : 'Access Token Active') : 'Click Authorize with Intuit to complete OAuth handshake'}
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={handleAuthorize}
                            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs font-bold transition-colors shadow-sm flex items-center gap-1"
                        >
                            <span className="material-symbols-outlined text-[15px]">login</span>
                            {account.IsConnected ? 'Re-Authorize' : 'Authorize with Intuit'}
                        </button>
                    </div>

                    {!showEdit ? (
                        <div className="space-y-3 bg-slate-900/60 p-4 rounded-lg border border-slate-700/60">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <span className="text-slate-400 block mb-0.5">QuickBooks Realm ID (Company ID):</span>
                                    <span className="font-mono text-white font-semibold text-xs">{account.RealmID || 'Pending Initial Auth'}</span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block mb-0.5">Company Name:</span>
                                    <span className="text-white font-medium">{account.CompanyName || 'Not Connected Yet'}</span>
                                </div>
                            </div>

                            <div className="pt-2 border-t border-slate-800">
                                <span className="text-slate-400 block mb-0.5">Client ID:</span>
                                <span className="font-mono text-slate-300 break-all">{account.ClientID}</span>
                            </div>

                            <div className="pt-2 border-t border-slate-800">
                                <span className="text-slate-400 block mb-0.5">Redirect URI:</span>
                                <span className="font-mono text-slate-300 break-all">{account.RedirectUri || 'https://admin.sbs-roc.com/api/quickbooks/callback'}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-800">
                                <div>
                                    <span className="text-slate-400 block mb-0.5">Access Token Status:</span>
                                    <span className="text-slate-300">
                                        {account.TokenExpiresAt ? `Expires: ${new Date(account.TokenExpiresAt).toLocaleString()}` : 'No Active Token'}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-slate-400 block mb-0.5">Refresh Token Status:</span>
                                    <span className="text-slate-300">
                                        {account.RefreshTokenExpiresAt ? `Expires: ${new Date(account.RefreshTokenExpiresAt).toLocaleDateString()}` : 'No Active Refresh Token'}
                                    </span>
                                </div>
                            </div>

                            <div className="pt-3 flex justify-between items-center border-t border-slate-800">
                                <button
                                    onClick={handleTestConnection}
                                    disabled={loadingTest || !account.IsConnected}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 rounded text-xs font-semibold flex items-center gap-1 transition-colors border border-slate-700"
                                >
                                    <span className="material-symbols-outlined text-[15px]">network_check</span>
                                    {loadingTest ? 'Testing...' : 'Test Connection'}
                                </button>
                                <button
                                    onClick={() => setShowEdit(true)}
                                    className="text-blue-400 hover:underline flex items-center gap-1 font-medium"
                                >
                                    <span className="material-symbols-outlined text-[15px]">edit</span>
                                    Edit Credentials
                                </button>
                            </div>

                            {testResult && (
                                <div className={`p-3 rounded border text-xs mt-2 ${testResult.success ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' : 'bg-red-950/60 border-red-800 text-red-300'}`}>
                                    {testResult.success ? (
                                        <div>
                                            <div className="font-bold flex items-center gap-1 mb-1">
                                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                                Connection Verified!
                                            </div>
                                            <div>Company: <strong>{testResult.companyInfo?.CompanyName}</strong></div>
                                            <div className="text-[11px] opacity-80 font-mono">Legal Name: {testResult.companyInfo?.LegalName || 'N/A'}</div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div className="font-bold flex items-center gap-1 mb-1">
                                                <span className="material-symbols-outlined text-[16px]">error</span>
                                                Connection Failed
                                            </div>
                                            <div>{testResult.error}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={handleSaveEdit} className="space-y-3 bg-slate-900/60 p-4 rounded-lg border border-slate-700/60">
                            <div>
                                <label className="block text-slate-400 mb-1">Account Label</label>
                                <input
                                    type="text"
                                    required
                                    value={editForm.accountName}
                                    onChange={e => setEditForm({ ...editForm, accountName: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-slate-400 mb-1">Environment</label>
                                    <select
                                        value={editForm.environment}
                                        onChange={e => setEditForm({ ...editForm, environment: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white focus:outline-none focus:border-blue-500"
                                    >
                                        <option value="sandbox">Sandbox (Development)</option>
                                        <option value="production">Production (Live)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-slate-400 mb-1">Realm ID (Optional)</label>
                                    <input
                                        type="text"
                                        placeholder="Auto-assigned during OAuth"
                                        value={editForm.realmId}
                                        onChange={e => setEditForm({ ...editForm, realmId: e.target.value })}
                                        className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white font-mono focus:outline-none focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-slate-400 mb-1">Client ID</label>
                                <input
                                    type="text"
                                    required
                                    value={editForm.clientId}
                                    onChange={e => setEditForm({ ...editForm, clientId: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-slate-400 mb-1">Client Secret (Leave blank to keep existing)</label>
                                <input
                                    type="password"
                                    placeholder="Enter new secret if changing"
                                    value={editForm.clientSecret}
                                    onChange={e => setEditForm({ ...editForm, clientSecret: e.target.value })}
                                    className="w-full bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-blue-500"
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowEdit(false)}
                                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded text-xs font-semibold"
                                >
                                    {saving ? 'Saving...' : 'Update Credentials'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-700 flex justify-end bg-slate-800/90">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
